import React, { useCallback, useMemo, useState } from 'react';
import { Linking, Platform, useWindowDimensions, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import HtmlRenderer from 'react-native-render-html';

import {
	PEOPLE_COMPANY_DOCUMENT_FORMAT,
	resolvePeopleCompanyDocumentMode,
} from '../../../common/utils/people/companyDocumentFormat';
import { isCompanyDocumentContractCategoryKey } from '../../../common/utils/people/companyDocumentCategories';
import {
	generateCompanyDocumentFormPdfBytes,
} from '../../../common/utils/people/companyDocumentFormPdf';
import {
	isAnonymousCompanyDocumentForm,
	resolveCompanyDocumentFormAnswerStatus,
} from '../../../common/utils/people/companyDocumentForm';
import {
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS,
} from '../../../common/constants';
import { useAppContext } from '../../../common/utils/context';
import {
	ACKNOWLEDGE_COMPANY_DOCUMENT_MOBILE,
	GET_MY_EMPLOYEE_PROFILE_MOBILE,
} from '../../graphql/people';
import {
	getPeopleSignedDownloadUrlMobile,
	uploadPeoplePdfBytes,
	uploadPeoplePngDataUrl,
} from '../../utils/peopleFileUpload';
import { SwitchWithLabel, Text } from '../../components';
import { SignaturePad, TextInput } from '../../components/input';
import {
	Card,
	PrimaryButton,
	Screen,
	SecondaryButton,
	SectionTitle,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { getHtmlBoldStyle } from '../../utils/htmlBoldStyle';
import CompanyDocumentFormQuestionFields from './CompanyDocumentFormQuestionFields';
import { createMyDocumentViewerStyles } from './MyDocumentViewerView.styles';

const DOCUMENT_HTML_IGNORED_STYLES = [
	'background',
	'background-color',
	'color',
];

const ACKNOWLEDGEABLE_STATUSES = [
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.REQUIRED,
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.OVERDUE,
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.OPTIONAL,
];

const resolveAssignmentRequiresSignature = ({ assignment, isContract }) => {
	if (assignment?.requiresSignature === true || assignment?.requiresSignature === false) {
		return assignment.requiresSignature;
	}

	return Boolean(assignment?.requirement?.requiresSignature || isContract);
};

// Mirrors the People web signature metadata (platform/timezone details stored with the ack).
const buildSignatureMeta = () => ({
	userAgent: `Sentry mobile (${Platform.OS})`,
	platform: Platform.OS,
	language: '',
	timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
	signedAt: new Date().toISOString(),
});

const getEmployeeDisplayName = employee => (
	[employee?.preferredName || employee?.firstName, employee?.lastName]
		.filter(Boolean)
		.join(' ')
		.trim() || employee?.personalEmail || 'Employee'
);

const showDocumentToast = message => Toast.show(message, {
	position: Toast.positions.TOP,
});

// Company document viewer for the signed-in employee: renders rich text or opens the
// linked file, handles form completion, and handles plain or drawn-signature acknowledgement.
// Template-field contracts stay in the People web app because they require PDF merging.
const MyDocumentViewerView = ({ route }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createMyDocumentViewerStyles);
	const { width } = useWindowDimensions();
	const { theme: { ui } } = useTheme();
	const htmlTagStyles = useMemo(() => {
		const bold = getHtmlBoldStyle(ui);
		return {
			p: { color: ui.colors.text },
			li: { color: ui.colors.text },
			strong: bold,
			b: bold,
		};
	}, [ui]);
	const { getServerEndpoint } = useAppContext();

	const assignment = route?.params?.assignment || null;
	const companyDocument = assignment?.companyDocument || route?.params?.document || null;

	const [acknowledgeDocument] = useMutation(ACKNOWLEDGE_COMPANY_DOCUMENT_MOBILE);
	const { data: profileData } = useQuery(GET_MY_EMPLOYEE_PROFILE_MOBILE, {
		fetchPolicy: 'cache-first',
	});
	const employee = profileData?.getMyEmployeeProfile || null;

	const [confirmed, setConfirmed] = useState(false);
	const [signatureName, setSignatureName] = useState('');
	const [signatureDataUrl, setSignatureDataUrl] = useState('');
	const [formAnswersById, setFormAnswersById] = useState({});
	const [showFormValidation, setShowFormValidation] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isOpeningFile, setIsOpeningFile] = useState(false);
	const [isAcknowledged, setIsAcknowledged] = useState(false);

	const documentMode = resolvePeopleCompanyDocumentMode(companyDocument);
	const isFormDocument = documentMode === PEOPLE_COMPANY_DOCUMENT_FORMAT.FORM;
	const isContract = isCompanyDocumentContractCategoryKey(companyDocument?.categoryKey);
	const requiresSignature = !isFormDocument && resolveAssignmentRequiresSignature({ assignment, isContract });
	const templateConfig = isFormDocument ? null : companyDocument?.templateConfig;
	const hasTemplateFields = Boolean(templateConfig?.fields?.length);
	const requiresPeopleApp = hasTemplateFields;
	const formConfig = isFormDocument ? companyDocument?.formConfig : null;
	const isAnonymousForm = isFormDocument && isAnonymousCompanyDocumentForm(formConfig);
	const formAnswerStatus = useMemo(() => resolveCompanyDocumentFormAnswerStatus({
		formConfig,
		formAnswers: formAnswersById,
	}), [formAnswersById, formConfig]);

	const displayTitle = String(assignment?.issuedTitle || '').trim() ||
		companyDocument?.title ||
		'Document';
	const contentHtml = isFormDocument ? '' : (
		String(assignment?.issuedContentHtml || '').trim() ||
		String(companyDocument?.contentHtml || '').trim()
	);
	const fileS3Key = isFormDocument ? '' : (companyDocument?.s3Key || '');

	const canAcknowledge = Boolean(assignment?.id &&
		!isAcknowledged &&
		!requiresPeopleApp &&
		ACKNOWLEDGEABLE_STATUSES.includes(Number(assignment?.status)));

	const openFile = useCallback(async () => {
		setIsOpeningFile(true);
		try {
			const signedUrl = await getPeopleSignedDownloadUrlMobile({
				scope: 'company',
				s3Key: fileS3Key,
				fileName: displayTitle,
				getServerEndpoint,
			});

			if (!signedUrl) {
				showDocumentToast('Unable to open document');
				return;
			}

			await Linking.openURL(signedUrl);
		}
		catch (error) {
			console.error('Company document open failed', error);
			showDocumentToast('Unable to open document');
		}
		finally {
			setIsOpeningFile(false);
		}
	}, [displayTitle, fileS3Key, getServerEndpoint]);

	const updateFormAnswer = useCallback((questionId, value) => {
		setFormAnswersById(previousAnswers => ({
			...previousAnswers,
			[questionId]: {
				...(previousAnswers?.[questionId] || {}),
				value,
			},
		}));
	}, []);

	const updateFormComment = useCallback((questionId, comment) => {
		setFormAnswersById(previousAnswers => ({
			...previousAnswers,
			[questionId]: {
				...(previousAnswers?.[questionId] || {}),
				comment,
			},
		}));
	}, []);

	const submitAcknowledgement = useCallback(async () => {
		if (!assignment?.id) {
			return;
		}
		if (isFormDocument && !formAnswerStatus.isComplete) {
			setShowFormValidation(true);
			showDocumentToast(`Complete required form questions: ${formAnswerStatus.missingRequiredLabels.join(', ')}.`);
			return;
		}

		setIsSubmitting(true);
		try {
			let signatureS3Key;
			let signedDocumentS3Key;

			if (requiresSignature) {
				if (!employee?.id) {
					showDocumentToast('Employee profile missing');
					return;
				}

				signatureS3Key = await uploadPeoplePngDataUrl({
					dataUrl: signatureDataUrl,
					fileName: 'signature.png',
					employeeId: employee.id,
					getServerEndpoint,
				});

				if (!signatureS3Key) {
					showDocumentToast('Signature upload failed');
					return;
				}
			}

			if (isFormDocument && !isAnonymousForm) {
				if (!employee?.id) {
					showDocumentToast('Employee profile missing');
					return;
				}

				try {
					const pdfBytes = await generateCompanyDocumentFormPdfBytes({
						title: displayTitle,
						description: formAnswerStatus.description,
						questionAnswers: formAnswerStatus.answerRecords,
						employeeName: getEmployeeDisplayName(employee),
						answeredAt: new Date(),
					});
					signedDocumentS3Key = await uploadPeoplePdfBytes({
						pdfBytes,
						fileName: 'form-response.pdf',
						employeeId: employee.id,
						getServerEndpoint,
					});
				}
				catch (error) {
					console.error('Company document form PDF generation failed', error);
					showDocumentToast('Unable to prepare form response');
					return;
				}

				if (!signedDocumentS3Key) {
					showDocumentToast('Form response upload failed');
					return;
				}
			}

			await acknowledgeDocument({
				variables: {
					input: {
						assignmentId: assignment.id,
						signatureName: requiresSignature ? signatureName.trim() : undefined,
						signatureS3Key: requiresSignature ? signatureS3Key : undefined,
						signedDocumentS3Key: signedDocumentS3Key || undefined,
						signatureMeta: isAnonymousForm ? undefined : buildSignatureMeta(),
						formAnswers: isFormDocument ? formAnswerStatus.sanitizedFormAnswerMap : undefined,
					},
				},
			});

			setIsAcknowledged(true);
			showDocumentToast(isFormDocument
				? 'Form submitted'
				: (requiresSignature ? 'Document signed' : 'Document acknowledged'));
			navigation.goBack();
		}
		catch (error) {
			console.error('Company document acknowledgement failed', error);
			showDocumentToast(error?.message || (isFormDocument ? 'Unable to submit form' : 'Unable to acknowledge document'));
		}
		finally {
			setIsSubmitting(false);
		}
	}, [
		acknowledgeDocument,
		assignment?.id,
		displayTitle,
		employee,
		formAnswerStatus,
		getServerEndpoint,
		isAnonymousForm,
		isFormDocument,
		navigation,
		requiresSignature,
		signatureDataUrl,
		signatureName,
	]);

	if (!companyDocument) {
		return null;
	}

	const canSubmit = isFormDocument || (requiresSignature
		? Boolean(signatureName.trim() && signatureDataUrl)
		: confirmed);
	const isWaived = Number(assignment?.status) === PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.WAIVED;
	const closedAt = isWaived ? assignment?.waivedAt : (assignment?.resolvedAt || (isAcknowledged ? new Date() : null));
	// A finished form (or a document with no text or file) has nothing else to show:
	// say when it was completed instead of leaving the page blank.
	const showClosedCard = !contentHtml && !fileS3Key && !canAcknowledge && !requiresPeopleApp;
	const subheading = [
		companyDocument.categoryLabel || 'Company document',
		assignment?.issuedAt ? `Issued ${moment(assignment.issuedAt).format('D MMM YYYY')}` : '',
		assignment?.dueAt && canAcknowledge ? `Due ${moment(assignment.dueAt).format('D MMM YYYY')}` : '',
	].filter(Boolean).join(' · ');

	return (
		<Screen
			heading={displayTitle}
			subheading={subheading}
		>
			{
				contentHtml ? (
					<Card>
						<HtmlRenderer
							baseFontStyle={styles.htmlBase}
							imagesMaxWidth={Math.max(1, Math.min(width, ui.layout.maxContentWidth) - 70)}
							html={contentHtml}
							ignoredStyles={DOCUMENT_HTML_IGNORED_STYLES}
							tagsStyles={htmlTagStyles}
						/>
					</Card>
				) : null
			}

			{
				fileS3Key ? (
					<Card>
						<Text style={styles.mutedText}>This document is a file attachment.</Text>
						<SecondaryButton
							label={isOpeningFile ? 'Opening…' : 'Open document'}
							icon="external-link"
							onPress={openFile}
							disabled={isOpeningFile}
							style={styles.cardButton}
						/>
					</Card>
				) : null
			}

			{
				showClosedCard ? (
					<Card>
						<SectionTitle
							title={isWaived ? 'No longer required' : (isFormDocument ? 'Form completed' : 'Document completed')}
							style={styles.cardTitle}
						/>
						<Text style={styles.mutedText}>
							{[
								closedAt ? `${isWaived ? 'Marked not required' : 'Completed'} on ${moment(closedAt).format('D MMM YYYY')}.` : '',
								isWaived ? 'There is nothing to do for this document.' : (isFormDocument ? 'Your answers have been submitted.' : 'There is nothing more to do for this document.'),
							].filter(Boolean).join(' ')}
						</Text>
					</Card>
				) : null
			}

			{
				assignment && requiresPeopleApp && !isAcknowledged &&
				ACKNOWLEDGEABLE_STATUSES.includes(Number(assignment.status)) ? (
						<Card tone="warning">
							<Text style={styles.noticeText}>
								This document needs details filled in before signing. Please complete it in Sentry People on the web.
							</Text>
						</Card>
					) : null
			}

			{
				canAcknowledge && (
					<Card>
						<SectionTitle
							title={isFormDocument ? 'Complete form' : (requiresSignature ? 'Sign document' : 'Acknowledge document')}
							style={styles.cardTitle}
						/>
						{
							isFormDocument ? (
								<>
									{isAnonymousForm && (
										<Card tone="current" style={styles.privacyNotice}>
											<Text style={styles.privacyNoticeText}>
												Your answers are anonymous. Sentry records that you completed this form, but your answers are not linked to your name.
											</Text>
										</Card>
									)}
									<CompanyDocumentFormQuestionFields
										description={formAnswerStatus.description}
										questions={formAnswerStatus.questions}
										answersById={formAnswersById}
										onChangeAnswer={updateFormAnswer}
										onChangeComment={updateFormComment}
										disabled={isSubmitting}
										showValidation={showFormValidation}
									/>
									<Text style={styles.formFooterNote}>
										{isAnonymousForm
											? 'Your completion is tracked separately from your anonymous answers.'
											: 'A PDF copy is generated automatically when you submit this form.'}
									</Text>
								</>
							) : requiresSignature ? (
								<>
									<View style={styles.formField}>
										<Text style={styles.formLabel}>Your full name</Text>
										<TextInput
											value={signatureName}
											onChange={setSignatureName}
											placeholder="Full name"
										/>
									</View>
									<View style={styles.formField}>
										<Text style={styles.formLabel}>Signature</Text>
										<SignaturePad
											value={signatureDataUrl}
											onChange={setSignatureDataUrl}
										/>
									</View>
								</>
							) : (
								<SwitchWithLabel
									label="I have read and understood this document"
									value={confirmed}
									onValueChange={setConfirmed}
								/>
							)
						}
						<PrimaryButton
							label={isSubmitting
								? 'Submitting…'
								: (isFormDocument ? 'Submit form' : (requiresSignature ? 'Sign and acknowledge' : 'Acknowledge'))}
							onPress={submitAcknowledgement}
							disabled={isSubmitting || !canSubmit}
							size="compact"
							style={styles.submitButton}
						/>
					</Card>
				)
			}
		</Screen>
	);
};

export default MyDocumentViewerView;
